import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Link } from "react-router-dom";

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-10px) rotate(1deg); }
`;

const pulseGlow = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.05); }
`;

const floatBlob = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, -45px) scale(1.08); }
`;

export const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  color: #0f172a;
  font-family: "Outfit", "Inter", sans-serif;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-x: hidden;
`;

export const Blob1 = styled.div`
  position: absolute;
  top: -10%;
  left: -10%;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(242, 50, 54, 0.05) 0%, rgba(242, 50, 54, 0) 70%);
  border-radius: 50%;
  filter: blur(80px);
  animation: ${floatBlob} 20s ease-in-out infinite;
  pointer-events: none;
`;

export const Blob2 = styled.div`
  position: absolute;
  top: 40%;
  right: -10%;
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(181, 35, 38, 0.03) 0%, rgba(181, 35, 38, 0) 70%);
  border-radius: 50%;
  filter: blur(100px);
  animation: ${floatBlob} 25s ease-in-out infinite alternate;
  pointer-events: none;
`;

// 🔹 Navigation Header
export const Header = styled.header`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 100;
  box-sizing: border-box;
`;

export const Logo = styled.img`
  height: 32px;
  object-fit: contain;
  filter: none; /* Make sure logo is visible against light background */
`;

export const NavLinks = styled.nav`
  display: flex;
  gap: 24px;

  @media (max-width: 600px) {
    display: none;
  }
`;

export const NavLink = styled.a`
  color: #475569;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #ff3135;
  }
`;

// 🔹 Hero Section
export const HeroContainer = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 24px 100px;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 60px;
  align-items: center;
  position: relative;
  z-index: 10;
  box-sizing: border-box;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding-top: 20px;
    padding-bottom: 60px;
    gap: 40px;
  }
`;

export const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 960px) {
    align-items: center;
    text-align: center;
  }
`;

export const HeroRight = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

export const TagBadge = styled.div`
  background: rgba(255, 49, 53, 0.08);
  border: 1px solid rgba(255, 49, 53, 0.15);
  color: #ff3135;
  padding: 6px 16px;
  border-radius: 99px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
  box-shadow: 0 2px 10px rgba(255, 49, 53, 0.05);
`;

export const MainHeading = styled.h1`
  font-size: 52px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1.5px;
  margin: 0 0 20px 0;
  background: linear-gradient(135deg, #0f172a 40%, #e11d48 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 600px) {
    font-size: 38px;
  }
`;

export const SubHeading = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #475569;
  margin: 0 0 40px 0;
  max-width: 520px;

  @media (max-width: 960px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

// 🔹 Referral Card In Hero
export const ReferralBox = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 28px;
  padding: 28px;
  box-sizing: border-box;
  margin-bottom: 32px;
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  text-align: left;

  &:hover {
    border-color: rgba(255, 49, 53, 0.25);
    box-shadow: 0 20px 40px rgba(255, 49, 53, 0.08);
    transform: translateY(-2px);
  }
`;

export const ReferralLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  display: block;
  margin-bottom: 8px;
`;

export const CodeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const ReferralCode = styled.span`
  font-size: 32px;
  font-weight: 800;
  color: #ff3135;
  letter-spacing: 2px;
  font-family: 'Courier New', Courier, monospace;
`;

export const CopyButton = styled.button`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  cursor: pointer;
  padding: 10px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: #0f172a;
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:active {
    transform: scale(0.92);
  }
`;

export const CopiedText = styled.span<{ visible: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  margin-top: 10px;
  display: block;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => (props.visible ? "0" : "4px")});
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

// 🔹 Primary Call to Action Button
export const ActionSection = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  gap: 16px;
  
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const PrimaryButton = styled.a`
  flex: 1;
  background: linear-gradient(135deg, #ff3135 0%, #b52326 100%);
  color: #ffffff;
  border-radius: 99px;
  padding: 16px 24px;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 10px 30px rgba(242, 50, 54, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 15px 40px rgba(242, 50, 54, 0.35);
    background: linear-gradient(135deg, #ff474b 0%, #d82028 100%);
  }
`;

// 🔹 Interactive Voucher Mockup Card
export const VoucherCard = styled.div`
  width: 340px;
  height: 200px;
  background: linear-gradient(135deg, #ffffff 0%, #fff5f5 100%);
  border: 1px solid rgba(255, 49, 53, 0.12);
  border-radius: 24px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 20px 40px rgba(255, 49, 53, 0.06), 0 1px 3px rgba(0, 0, 0, 0.05);
  position: relative;
  overflow: hidden;
  animation: ${float} 6s ease-in-out infinite;

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255, 49, 53, 0.08) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 960px) {
    display: none;
  }
`;

export const VoucherGlow = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 24px;
  border: 1px solid rgba(255, 49, 53, 0.2);
  opacity: 0.4;
  animation: ${pulseGlow} 4s ease-in-out infinite;
  pointer-events: none;
`;

export const VoucherHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const VoucherBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #ff3135;
  letter-spacing: 1.5px;
`;

export const VoucherValue = styled.h3`
  font-size: 36px;
  font-weight: 800;
  margin: 0;
  color: #0f172a;
  letter-spacing: -1px;
  line-height: 1;
`;

export const VoucherFooter = styled.p`
  font-size: 13px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

// 🔹 "How It Works" Section
export const SectionContainer = styled.section`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  box-sizing: border-box;
  position: relative;
  z-index: 10;
`;

export const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.5px;
  margin: 0 0 48px 0;
  color: #0f172a;
`;

export const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

export const StepCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 36px 30px;
  box-sizing: border-box;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.03), 0 2px 4px -2px rgba(0, 0, 0, 0.03);

  &:hover {
    background: #ffffff;
    border-color: rgba(255, 49, 53, 0.25);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
    transform: translateY(-4px);
  }
`;

export const StepIconWrapper = styled.div`
  width: 56px;
  height: 56px;
  background: rgba(255, 49, 53, 0.06);
  border: 1px solid rgba(255, 49, 53, 0.12);
  border-radius: 16px;
  color: #ff3135;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
`;

export const StepTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #0f172a;
`;

export const StepDescription = styled.p`
  font-size: 14px;
  line-height: 1.5;
  color: #475569;
  margin: 0;
`;

// 🔹 Footer Section
export const FooterContainer = styled.footer`
  width: 100%;
  border-top: 1px solid #e2e8f0;
  background-color: #ffffff;
  position: relative;
  z-index: 10;
  padding: 60px 24px 40px;
  box-sizing: border-box;
`;

export const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

export const FooterLink = styled(Link)`
  color: #475569;
  text-decoration: none;
  font-size: 13px;
  font-weight: 500;
  transition: color 0.2s ease;

  &:hover {
    color: #ff3135;
  }
`;

export const Copyright = styled.p`
  font-size: 13px;
  color: #64748b;
  text-align: center;
  margin: 0;
  border-top: 1px solid #f1f5f9;
  padding-top: 24px;
`;
